import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Radio, CheckCircle, FileText, Archive } from 'lucide-react';
import EmployeePortalCrudShell from './EmployeePortalCrudShell';
import { ANNOUNCEMENT_STATUS, ANNOUNCEMENT_CATEGORIES, statusBadge, defaultStats } from './employeePortalShared';
import { formatPortalDateWithHijri, resolveOptionLabel } from './employeePortalSaudiFormat';
import { usePortalEmployeeContext } from './PortalEmployeeContext';

const NS = 'announcementsPage';
const API = '/api/employee-portal/announcements';

const EMPTY = {
  announcement_code: '', title: '', content: '', category: '', audience: 'all', status: 'brouillon',
  publish_date: '', expiry_date: '', author: '', is_pinned: '', notes: '',
};

export default function EmployeePortalAnnouncementsPage() {
  const { i18n } = useTranslation();
  const { isAdmin, name } = usePortalEmployeeContext();
  const apiQueryParams = useMemo(
    () => (isAdmin ? {} : { published_only: '1' }),
    [isAdmin],
  );

  const catOpts = ANNOUNCEMENT_CATEGORIES.map((c) => ({ value: c.value, labelKey: c.labelKey }));
  const statusOpts = ANNOUNCEMENT_STATUS.map((s) => ({ value: s.value, labelKey: s.labelKey }));

  const renderCategory = (r, _p, _t, pl) => resolveOptionLabel(r.category, ANNOUNCEMENT_CATEGORIES, pl);

  const renderPublishDate = (r) => {
    const date = r.publish_date;
    if (!date) return '—';
    return formatPortalDateWithHijri(date, i18n.language);
  };

  const formSections = useMemo(() => {
    const announcementFields = [
      { key: 'title', labelKey: 'field_title', required: true },
      { key: 'category', labelKey: 'field_category', type: 'select', options: catOpts },
      { key: 'status', labelKey: 'field_status', type: 'select', options: statusOpts },
      { key: 'publish_date', labelKey: 'field_publish', type: 'date' },
      { key: 'expiry_date', labelKey: 'field_expiry', type: 'date' },
    ];
    if (isAdmin) {
      announcementFields.push({ key: 'is_pinned', labelKey: 'field_pinned', type: 'select', options: [
        { value: '', labelKey: 'pinned_no' },
        { value: '1', labelKey: 'pinned_yes' },
      ] });
    }
    return [
      {
        id: 'announcement',
        titleKey: 'section_announcement',
        fields: announcementFields,
      },
      {
        id: 'content',
        titleKey: 'section_content',
        fields: [
          { key: 'content', labelKey: 'field_content', type: 'textarea' },
          { key: 'notes', labelKey: 'field_notes', type: 'textarea' },
        ],
      },
    ];
  }, [isAdmin, catOpts, statusOpts]);

  return (
    <EmployeePortalCrudShell
      ns={NS}
      icon={Radio}
      apiPath={API}
      apiQueryParams={apiQueryParams}
      codeField="announcement_code"
      entityPrefixKey="employeePortalAnnouncement"
      codePrefixFallback="EPA"
      allowCreate={isAdmin}
      allowEdit={isAdmin}
      allowDelete={isAdmin}
      gradient="from-rose-500 to-rose-700"
      emptyForm={EMPTY}
      mapRecordToForm={(r) => ({
        announcement_code: r.announcement_code ?? '',
        title: r.title ?? '',
        content: r.content ?? '',
        category: r.category ?? '',
        audience: r.audience ?? 'all',
        status: r.status ?? 'brouillon',
        publish_date: r.publish_date ?? '',
        expiry_date: r.expiry_date ?? '',
        author: r.author ?? '',
        is_pinned: r.is_pinned ? '1' : '',
        notes: r.notes ?? '',
      })}
      mapFormToPayload={(f) => ({
        announcement_code: f.announcement_code || null,
        title: f.title?.trim(),
        content: f.content?.trim() || null,
        category: f.category || null,
        audience: f.audience || 'all',
        status: f.status || 'brouillon',
        publish_date: f.publish_date || null,
        expiry_date: f.expiry_date || null,
        author: f.author || name || null,
        is_pinned: f.is_pinned === '1' || f.is_pinned === 1 ? 1 : 0,
        notes: f.notes?.trim() || null,
      })}
      computeStats={(records) => defaultStats(records, {
        totalIcon: Radio,
        fields: [
          { titleKey: 'stat_published', field: 'status', value: 'publie', icon: CheckCircle, color: 'from-green-500 to-green-600' },
          { titleKey: 'stat_draft', field: 'status', value: 'brouillon', icon: FileText, color: 'from-amber-500 to-amber-600' },
          { titleKey: 'stat_archived', field: 'status', value: 'archive', icon: Archive, color: 'from-gray-500 to-gray-600' },
        ],
      })}
      columns={[
        { key: 'announcement_code', labelKey: 'col_code' },
        { key: 'title', labelKey: 'col_title' },
        { key: 'category', labelKey: 'col_category', render: renderCategory },
        { key: 'status', labelKey: 'col_status', render: (r, _p, _t, pl) => statusBadge(r.status, pl, ANNOUNCEMENT_STATUS) },
        { key: 'publish_date', labelKey: 'col_publish', render: renderPublishDate },
      ]}
      formSections={formSections}
    />
  );
}
